import { toURLSearchParams } from '@wix/sdk-runtime/rest-modules';
import { transformSDKTimestampToRESTTimestamp } from '@wix/sdk-runtime/transformations/timestamp';
import { transformRESTTimestampToSDKTimestamp } from '@wix/sdk-runtime/transformations/timestamp';
import { transformSDKFieldMaskToRESTFieldMask } from '@wix/sdk-runtime/transformations/field-mask';
import { transformPaths } from '@wix/sdk-runtime/transformations/transform-paths';
import { resolveUrl } from '@wix/sdk-runtime/rest-modules';
import { ResolveUrlOpts } from '@wix/sdk-runtime/rest-modules';
import { RequestOptionsFactory } from '@wix/sdk-types';

function resolveWixFormsAiV1ChatSettingsServiceUrl(
  opts: Omit<ResolveUrlOpts, 'domainToMappings'>
) {
  const domainToMappings = {
    'bo._base_domain_': [
      {
        srcPath: '/_api/form-ai-assistant',
        destPath: '',
      },
    ],
    'wixbo.ai': [
      {
        srcPath: '/_api/form-ai-assistant',
        destPath: '',
      },
    ],
    'wix-bo.com': [
      {
        srcPath: '/_api/form-ai-assistant',
        destPath: '',
      },
    ],
    '*.wixforms.com': [
      {
        srcPath: '/_api/form-ai-assistant',
        destPath: '',
      },
    ],
    'manage._base_domain_': [
      {
        srcPath: '/_api/form-ai-assistant',
        destPath: '',
      },
    ],
    _: [
      {
        srcPath: '/_api/form-ai-assistant',
        destPath: '',
      },
    ],
    'www.wixapis.com': [
      {
        srcPath: '/forms/ai/v1/chat-settings',
        destPath: '/v1/chat-settings',
      },
      {
        srcPath: '/forms/ai/v1/interactive-form-sessions',
        destPath: '/v1/interactive-form-sessions',
      },
    ],
    '*.dev.wix-code.com': [
      {
        srcPath: '/_api/form-ai-assistant',
        destPath: '',
      },
    ],
    'editor._base_domain_': [
      {
        srcPath: '/_api/form-ai-assistant',
        destPath: '',
      },
    ],
    'blocks._base_domain_': [
      {
        srcPath: '/_api/form-ai-assistant',
        destPath: '',
      },
    ],
    'create.editorx': [
      {
        srcPath: '/_api/form-ai-assistant',
        destPath: '',
      },
    ],
    'apps.wixel.com': [
      {
        srcPath: '/_api/form-ai-assistant',
        destPath: '',
      },
    ],
    '*.wixel.com': [
      {
        srcPath: '/_api/form-ai-assistant',
        destPath: '',
      },
    ],
  };

  return resolveUrl(Object.assign(opts, { domainToMappings }));
}

const PACKAGE_NAME = '@wix/auto_sdk_forms_chat-settings';

/**
 * Creates a chat settings entity.
 *
 * The settings specified in a chat settings entity apply to the form with the same ID as the chat settings entity. Each form can have exactly one set of chat settings.
 */
export function createChatSettings(
  payload: object
): RequestOptionsFactory<any> {
  function __createChatSettings({ host }: any) {
    const serializedData = transformPaths(payload, [
      {
        transformFn: transformSDKTimestampToRESTTimestamp,
        paths: [
          { path: 'chatSettings.createdDate' },
          { path: 'chatSettings.updatedDate' },
        ],
      },
    ]);
    const metadata = {
      entityFqdn: 'wix.forms.ai.v1.chat_settings',
      method: 'POST' as any,
      methodFqn: 'wix.forms.ai.v1.ChatSettingsService.CreateChatSettings',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveWixFormsAiV1ChatSettingsServiceUrl({
        protoPath: '/v1/chat-settings',
        data: serializedData,
        host,
      }),
      data: serializedData,
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [
              { path: 'chatSettings.createdDate' },
              { path: 'chatSettings.updatedDate' },
            ],
          },
        ]),
    };

    return metadata;
  }

  return __createChatSettings;
}

/**
 * Updates a chat settings entity.
 *
 * Changes take effect immediately for new interactive form sessions.
 */
export function updateChatSettings(
  payload: object
): RequestOptionsFactory<any> {
  function __updateChatSettings({ host }: any) {
    const serializedData = transformPaths(payload, [
      {
        transformFn: transformSDKFieldMaskToRESTFieldMask,
        paths: [{ path: 'fieldMask' }],
      },
      {
        transformFn: transformSDKTimestampToRESTTimestamp,
        paths: [
          { path: 'chatSettings.createdDate' },
          { path: 'chatSettings.updatedDate' },
        ],
      },
    ]);
    const metadata = {
      entityFqdn: 'wix.forms.ai.v1.chat_settings',
      method: 'PATCH' as any,
      methodFqn: 'wix.forms.ai.v1.ChatSettingsService.UpdateChatSettings',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveWixFormsAiV1ChatSettingsServiceUrl({
        protoPath: '/v1/chat-settings/{chatSettings.id}',
        data: serializedData,
        host,
      }),
      data: serializedData,
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [
              { path: 'chatSettings.createdDate' },
              { path: 'chatSettings.updatedDate' },
            ],
          },
        ]),
    };

    return metadata;
  }

  return __updateChatSettings;
}

/** Deletes a chat settings entity. */
export function deleteChatSettings(
  payload: object
): RequestOptionsFactory<any> {
  function __deleteChatSettings({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.forms.ai.v1.chat_settings',
      method: 'DELETE' as any,
      methodFqn: 'wix.forms.ai.v1.ChatSettingsService.DeleteChatSettings',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveWixFormsAiV1ChatSettingsServiceUrl({
        protoPath: '/v1/chat-settings/{chatSettingsId}',
        data: payload,
        host,
      }),
      params: toURLSearchParams(payload),
    };

    return metadata;
  }

  return __deleteChatSettings;
}

/** Retrieves a chat settings entity. */
export function getChatSettings(payload: object): RequestOptionsFactory<any> {
  function __getChatSettings({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.forms.ai.v1.chat_settings',
      method: 'GET' as any,
      methodFqn: 'wix.forms.ai.v1.ChatSettingsService.GetChatSettings',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveWixFormsAiV1ChatSettingsServiceUrl({
        protoPath: '/v1/chat-settings/{chatSettingsId}',
        data: payload,
        host,
      }),
      params: toURLSearchParams(payload),
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [
              { path: 'chatSettings.createdDate' },
              { path: 'chatSettings.updatedDate' },
            ],
          },
        ]),
    };

    return metadata;
  }

  return __getChatSettings;
}

/**
 * Retrieves a list of chat settings entities given the provided paging, filtering, and sorting.
 *
 * ### Defaults
 * Query Chat Settings has the following default settings, which you can override:
 * + Sorted by `id` in ascending order.
 * + `cursorPaging.limit` set to `100`.
 */
export function queryChatSettings(payload: object): RequestOptionsFactory<any> {
  function __queryChatSettings({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.forms.ai.v1.chat_settings',
      method: 'GET' as any,
      methodFqn: 'wix.forms.ai.v1.ChatSettingsService.QueryChatSettings',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveWixFormsAiV1ChatSettingsServiceUrl({
        protoPath: '/v1/chat-settings/query',
        data: payload,
        host,
      }),
      params: toURLSearchParams(payload, true),
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [
              { path: 'chatSettings.createdDate' },
              { path: 'chatSettings.updatedDate' },
            ],
          },
        ]),
      fallback: [
        {
          method: 'POST' as any,
          url: resolveWixFormsAiV1ChatSettingsServiceUrl({
            protoPath: '/v1/chat-settings/query',
            data: payload,
            host,
          }),
          data: payload,
        },
      ],
    };

    return metadata;
  }

  return __queryChatSettings;
}
